import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Loop } from '@/common/api/types/loops';
import type { Objective } from '@/common/api/types/objectives';
import { useFlag } from '@/common/hooks/feature-flags/useFlag';
import { useAllLoopsQuery } from '@/common/queries/useAllLoopsQuery';
import {
  NoRolesConfiguredError,
  RunAlreadyActiveError,
} from '@/features/agentic-insights/api/agenticInsightsApi';
import { useAgenticRunDataSource } from '@/features/agentic-insights/components/data/agenticRunDataSource';
import { FindingRefsProvider } from '@/features/agentic-insights/components/data/FindingRefsContext';
import { personasForRun } from '@/features/agentic-insights/components/data/personas';
import { resolvePersonaViews } from '@/features/agentic-insights/components/data/resolvePersonaView';
import type { SignalScope } from '@/features/agentic-insights/components/data/types';
import { useAgenticRun } from '@/features/agentic-insights/components/data/useAgenticRun';
import { useEffectiveRunStatus } from '@/features/agentic-insights/components/data/useEffectiveRunStatus';
import { useFindingRefs } from '@/features/agentic-insights/components/data/useFindingRefs';
import { usePersonasQuery } from '@/features/agentic-insights/components/data/usePersonas';
import { useRunList } from '@/features/agentic-insights/components/data/useRunList';
import { useTriggerAgenticRun } from '@/features/agentic-insights/components/data/useTriggerAgenticRun';
import { useViewedRun } from '@/features/agentic-insights/components/data/useViewedRun';
import {
  setFieldAnnotations,
  useFieldAnnotations,
} from '@/features/agentic-insights/components/experimental/shared/data/fieldAnnotations';
import {
  buildFindingEntries,
  countWithheld,
} from '@/features/agentic-insights/components/experimental/shared/data/findingEntries';
import { buildObjectiveBuckets } from '@/features/agentic-insights/components/experimental/shared/data/objectiveBuckets';
import type { VariantId } from '@/features/agentic-insights/components/experimental/variants/VariantPicker';
import { useObjectivesByLoopIdInfiniteQuery } from '@/features/objectives/queries';
import { useObjectiveHealthByIdsQuery } from '@/features/objectives/queries/useObjectiveHealthQuery';
import type { AggregateLoopRun } from './data/aggregateFindings';
import {
  buildAggregateReading,
  entryKey,
  filterEntries,
  useAggregateLatestRuns,
} from './data/aggregateFindings';
import {
  isNavScope,
  loopScopeToSearchParam,
  resolveLoopScope,
  validateScope,
} from './data/loopScope';
import type { ExperimentalSearch, InsightsSubview } from './data/searchState';
import { DetailView } from './findings/DetailView';
import { FindingList } from './findings/FindingList';
import { ContextBanner } from './header/ContextBanner';
import { RunSentence } from './header/RunSentence';
import { RunsCog } from './header/RunsCog';
import { MEASURE, TONE, TYPE } from './language';
import { FindingsViewPicker } from './nav/FindingsViewPicker';
import { NavigationBar } from './nav/NavigationBar';
import { RunState } from './states/RunState';
import { useRunGate } from './states/useRunGate';

const NO_OBJECTIVES: readonly Objective[] = [];
const NO_LOOPS: readonly Loop[] = [];

interface ExperimentalInsightsPageProps {
  loopId: number;
  org: string;
  search: ExperimentalSearch;
  /** Which variant this is, for the variant picker. The route owns it — the variant is the route. */
  variant: VariantId;
}

/**
 * Briefing #4: Briefing #3's reading, plus a loop switcher (NEX-18040). A run is already scoped to
 * one loop — the route fixes it, and findings carry no loop attribution of their own — so the useful
 * control here is not a filter on the run you have but a switch to another loop's run entirely.
 *
 * `scope` (`data/loopScope.ts`) is the one thing this variant adds to Briefing #3's state: a search
 * param layered over the nav loop the route fixes. Every other loop-aware piece of the page —
 * whether the run action is legal, what the context banner says, which loop's objectives resolve for
 * the meta line, whether findings are one loop's or every loop's merged — is derived from `scope`
 * plus `loopId`, never stored on its own.
 */
export const ExperimentalInsightsPage = ({
  loopId,
  org,
  search,
  variant,
}: ExperimentalInsightsPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dataSource, setDataSource } = useAgenticRunDataSource();
  const fieldKeys = useFieldAnnotations();

  const runControlsEnabled = useFlag('sirAgenticInsightsExpertMode');

  const allLoopsQuery = useAllLoopsQuery();
  const loops = allLoopsQuery.data ?? NO_LOOPS;
  const loopsLoaded = !allLoopsQuery.isLoading && allLoopsQuery.data !== undefined;
  const scope = validateScope(resolveLoopScope(search.loop, loopId), loopId, loops, loopsLoaded);

  const navScope = isNavScope(scope, loopId);
  const isAggregate = scope === null;
  /** The loop whose run a single-loop reading pulls from — the nav loop while `isAggregate`, since
   * there is no single loop in that mode and every single-loop hook still needs one to call with. */
  const focusedLoopId = scope ?? loopId;

  /** Told by `LoopSwitcher` whenever its popover opens or closes, purely to decide whether the
   * aggregate fan-out below is worth running — see that hook's own note. */
  const [loopSwitcherOpen, setLoopSwitcherOpen] = useState(false);

  // The fan-out is one `runs/latest` request per loop in the org, plus a poll timer each — real
  // cost on a large org, and one that buys nothing unless the reader is either in All loops mode
  // (where the merge needs every one of them) or has the switcher open (where each row's count is
  // the whole point). Gated on that rather than run unconditionally on every visit to this page.
  const aggregateRuns = useAggregateLatestRuns(
    isAggregate || loopSwitcherOpen ? loops : NO_LOOPS,
    dataSource,
  );
  const runsByLoopId = useMemo(
    () => new Map<number, AggregateLoopRun>(aggregateRuns.map((entry) => [entry.loop.id, entry])),
    [aggregateRuns],
  );
  const navLoopNameResolved = loops.find((loop) => loop.id === loopId)?.name;
  const focusedLoopNameResolved = loops.find((loop) => loop.id === focusedLoopId)?.name;
  // Everywhere except the context banner tolerates the name arriving a beat late (empty string
  // rather than "undefined" is enough while the cog and run gate are still loading their own data);
  // the banner is stricter about not showing a partial sentence, so it gets the raw, possibly-
  // unresolved values below instead.
  const navLoopName = navLoopNameResolved ?? '';
  const focusedLoopName = focusedLoopNameResolved ?? '';

  const { data: liveRun } = useAgenticRun(loopId);
  const {
    data: viewedRun,
    isLoading,
    isError,
    refetch,
  } = useViewedRun(focusedLoopId, search.runId);
  const { data: runList = [] } = useRunList(focusedLoopId);
  const { data: configuredPersonas } = usePersonasQuery();
  const triggerRun = useTriggerAgenticRun(loopId);

  // NEX-17688: every run is authored at the curated scope until the signals work resumes.
  const runMode: SignalScope = 'curated';

  const setSearch = (next: Partial<ExperimentalSearch>, options?: { push?: boolean }) =>
    navigate({
      to: '.',
      search: { ...search, ...next },
      // Changing reading or filter replaces the entry: back should leave the page, not walk a reader
      // through every tab they touched. Opening a entry pushes, so back closes it.
      replace: !options?.push,
    });

  /**
   * A run id and an objective both belong to one loop, so a scope change drops both rather than
   * carrying them into a loop where they mean nothing — the ticket's own requirement. The open finding
   * goes with them: a finding id is only meaningful against the run it came from.
   */
  const setScope = (next: number | null) =>
    setSearch({
      loop: loopScopeToSearchParam(next, loopId),
      runId: undefined,
      objective: undefined,
      finding: undefined,
    });

  /**
   * The deliberate second step past reading another loop: rewrites the path's own `$loopId` so the
   * reader is simply on that loop's briefing, with `?loop` dropped and re-running restored. Only the
   * reading mode carries over — everything else is a fresh arrival, the same as following a link to
   * that loop directly.
   */
  const switchToLoop = (targetLoopId: number) =>
    navigate({
      to: '/org/$org/loop/$loopId/agentic-insights/experimental/briefing-4',
      params: { org, loopId: targetLoopId },
      search: { view: search.view },
    });

  const corePersonaId = viewedRun?.personaId;
  const personas = useMemo(
    () =>
      corePersonaId
        ? personasForRun(configuredPersonas ?? [], corePersonaId)
        : (configuredPersonas ?? []),
    [configuredPersonas, corePersonaId],
  );

  const [selectedPersonaId, setSelectedPersonaId] = useState<string>();
  const personaId =
    selectedPersonaId && personas.some((persona) => persona.id === selectedPersonaId)
      ? selectedPersonaId
      : (corePersonaId ?? '');

  const singleViews = useMemo(
    () => resolvePersonaViews(viewedRun?.findings ?? [], personaId, corePersonaId ?? ''),
    [viewedRun, personaId, corePersonaId],
  );
  const singleEntries = useMemo(
    () => buildFindingEntries(viewedRun?.findings ?? [], singleViews),
    [viewedRun, singleViews],
  );
  const aggregateReading = useMemo(
    () => buildAggregateReading(aggregateRuns, personaId),
    [aggregateRuns, personaId],
  );

  const liveStatus = useEffectiveRunStatus(liveRun);
  const isRunning = triggerRun.isPending || liveStatus === 'running';
  const viewedStatus = useEffectiveRunStatus(viewedRun);
  const run = viewedRun ? { ...viewedRun, status: viewedStatus } : undefined;

  const findings = isAggregate ? aggregateReading.findings : (run?.findings ?? []);
  const views = isAggregate ? aggregateReading.views : singleViews;
  const allEntries = isAggregate ? aggregateReading.entries : singleEntries;
  const entries = useMemo(
    () => filterEntries(allEntries, search.objective),
    [allEntries, search.objective],
  );

  const objectivesQuery = useObjectivesByLoopIdInfiniteQuery({ loopId: focusedLoopId });
  const objectives = objectivesQuery.data ?? NO_OBJECTIVES;
  const buckets = useMemo(
    () => buildObjectiveBuckets(findings, objectives),
    [findings, objectives],
  );

  const refs = useFindingRefs(findings, focusedLoopId);

  // Every objective a finding on this reading names, so the meta line's objective-scope entry can
  // show its progress and health without a query per row. `useObjectiveHealthByIdsQuery` batches and
  // caches per id itself, and is not loop-scoped, so it resolves the same way whether `findings` is
  // one loop's or every loop's merged.
  const objectiveIdsOnFindings = useMemo(
    () => [...new Set(findings.flatMap((finding) => finding.objectiveIds.map(Number)))],
    [findings],
  );
  const { data: objectiveHealthById, isPending: isObjectiveHealthPending } =
    useObjectiveHealthByIdsQuery(objectiveIdsOnFindings);

  const subview: InsightsSubview = search.view ?? 'findings';

  const runSentence = (
    <RunSentence
      type={TYPE}
      personas={personas}
      personaId={personaId}
      corePersonaId={corePersonaId}
      onPersonaChange={setSelectedPersonaId}
      onManageRoles={() =>
        navigate({
          to: '/org/$org/loop/$loopId/agentic-insights/experimental/classic',
          params: { org, loopId },
        })
      }
      // The shared `countWithheld` walks raw findings against a `views` map keyed by `finding.id` —
      // correct for the single-loop reading, but the aggregate `views` map is keyed by `entryKey`
      // (loop-qualified) to avoid the cross-loop id collision, so aggregate mode counts its own
      // withheld findings while building that map instead (`buildAggregateReading`).
      withheldCount={isAggregate ? aggregateReading.withheldCount : countWithheld(findings, views)}
    />
  );

  const singleLoopGate = useRunGate({
    type: TYPE,
    tone: TONE,
    measure: MEASURE,
    run,
    viewedRun,
    requestedRunId: search.runId,
    isLoading,
    isError,
    isRunning,
    canRun: runControlsEnabled,
    isNavScope: navScope,
    switchedLoopName: focusedLoopName,
    navLoopName,
    onRetry: () => refetch(),
    onReadLatest: () => setSearch({ runId: undefined }),
    onRun: () => triggerRun.mutate(runMode),
    onSwitchToLoop: () => switchToLoop(focusedLoopId),
    onBackToNavLoop: () => setScope(loopId),
  });

  // All loops mode has no single run to gate on — the merge shows whatever every readable loop's
  // latest run carries, so the only "blocked" states left are the org's loop list and the fan-out
  // itself still loading, or the loop list failing outright.
  const aggregateStillLoading =
    allLoopsQuery.isLoading || aggregateRuns.some((entry) => entry.isLoading);
  // A single loop's `runs/latest` failing is treated the same as the loop-list query failing: the
  // merge would otherwise render whatever loops DID resolve as if that were the complete set, and
  // the banner's own finding/loop counts would agree with it — an under-count that looks like a
  // correct total. Blocking the whole reading is the more honest failure mode than a merge that
  // quietly has a hole in it.
  const aggregateHasError = allLoopsQuery.isError || aggregateRuns.some((entry) => entry.isError);
  // Every readable loop's latest run resolved with nothing to report — reachable only once loading
  // and error are ruled out and at least one loop is readable (an org with no readable loops at all
  // is a different, out-of-scope state — see `readableLoopRuns`).
  const aggregateEmpty =
    !aggregateHasError &&
    !aggregateStillLoading &&
    aggregateReading.readableLoopCount > 0 &&
    aggregateReading.findings.length === 0;
  const aggregateGate = aggregateHasError ? (
    <p className={`${TYPE.claim} ${MEASURE} py-16`} data-testid="agentic-x-state-read-error">
      {t(($) => $.loopAgenticInsights.experimental.states.readErrorBody)}
    </p>
  ) : aggregateStillLoading ? (
    <p className={`${TYPE.meta} py-16`} data-testid="agentic-x-state-loading">
      {t(($) => $.loopAgenticInsights.experimental.states.loading)}
    </p>
  ) : aggregateEmpty ? (
    <RunState
      type={TYPE}
      measure={MEASURE}
      testId="agentic-x-state-no-findings-aggregate"
      title={t(($) => $.loopAgenticInsights.experimental.states.noFindingsAggregateTitle)}
      body={t(($) => $.loopAgenticInsights.experimental.states.noFindingsAggregateBody)}
    />
  ) : undefined;

  const blocked = isAggregate ? aggregateGate : singleLoopGate;

  const triggerNotice = () => {
    if (!triggerRun.isError) return null;

    const message =
      triggerRun.error instanceof RunAlreadyActiveError
        ? t(($) => $.loopAgenticInsights.page.runAlreadyActiveDescription)
        : triggerRun.error instanceof NoRolesConfiguredError
          ? t(($) => $.loopAgenticInsights.page.noRolesConfiguredDescription)
          : t(($) => $.loopAgenticInsights.page.runInsightsFailedDescription);

    return (
      <p
        data-testid="agentic-x-trigger-error"
        className={`${TYPE.meta} border-l-2 border-border-error pl-3 text-text-error`}
      >
        {message}
      </p>
    );
  };

  const findingsViewPicker = (
    <FindingsViewPicker
      active={subview === 'detail' ? 'detail' : 'findings'}
      onChange={(view) =>
        setSearch({
          view,
          // Entering the detail with nothing chosen lands on the first entry rather than on an
          // empty pane; leaving it drops that choice again. Carried back to the list, the entry
          // the detail had picked for you arrived as a row already open — a selection the reader
          // never made.
          finding:
            view === 'detail'
              ? (search.finding ?? (entries[0] ? entryKey(entries[0]) : undefined))
              : undefined,
        })
      }
    />
  );

  const runsCog = (
    <RunsCog
      variant={variant}
      org={org}
      loopId={loopId}
      isAggregate={isAggregate}
      focusedLoopName={focusedLoopName}
      runs={runList}
      selectedRunId={search.runId}
      onSelectRun={(runId) => setSearch({ runId, finding: undefined })}
      isNavScope={navScope}
      offContextLoopName={focusedLoopName}
      navLoopName={navLoopName}
      navHasRun={liveRun !== null && liveRun !== undefined}
      canRun={runControlsEnabled}
      isRunning={isRunning}
      onRun={() => triggerRun.mutate(runMode)}
      sampleData={dataSource === 'mock'}
      onSampleDataChange={(sample) => setDataSource(sample ? 'mock' : 'real')}
      fieldKeys={fieldKeys}
      onFieldKeysChange={setFieldAnnotations}
    />
  );

  const reading = () => {
    if (!isAggregate && !run) return null;

    return subview === 'detail' ? (
      <DetailView
        entries={entries}
        views={views}
        objectiveHealthById={objectiveHealthById}
        isObjectiveHealthPending={isObjectiveHealthPending}
        selectedFindingId={search.finding}
        onSwitchLoop={isAggregate ? setScope : undefined}
        onSelectFinding={(finding) => setSearch({ finding })}
      />
    ) : (
      <FindingList
        entries={entries}
        views={views}
        objectiveHealthById={objectiveHealthById}
        isObjectiveHealthPending={isObjectiveHealthPending}
        openFindingId={search.finding}
        onSwitchLoop={isAggregate ? setScope : undefined}
        onOpenFinding={(finding) => setSearch({ finding }, { push: finding !== undefined })}
      />
    );
  };

  return (
    <FindingRefsProvider refs={refs}>
      {/* Cancels the shell's horizontal padding so the surface runs to the window edges, then
          reinstates its own — the header band and the reading share one gutter, and the band's rule
          runs the full width.

          The cursor rule is set once here rather than on each control: this surface builds its own
          primitives instead of using `sui-button`, which ships `cursor-pointer` of its own, so
          without it every row and tab would show an arrow and read as inert. */}
      <div className="-mx-4 flex h-full min-h-0 flex-col bg-bg-surface-foundation [&_[role=button]]:cursor-pointer [&_button:not(:disabled)]:cursor-pointer">
        {/* Nothing above the band's edge now — the role picker lives in the controls row below, and
            so does the runs/variant/settings cog, at the row's far right past List/Details.
            `NavigationBar` draws everything that acts on the reading: which role it is voiced as,
            which loop it is reading, the objective filter, and the mode/cog controls.
            `pt-4` matches this app's own toolbar-at-top-of-content convention (see `PortfolioToolbar`
            via `TwoColumnLayout`'s `pt-4`) rather than the taller spacing a real page header wants —
            this row IS the toolbar now, not a banner above one. */}
        <header className="shrink-0 px-6 pt-4">
          {triggerNotice()}
          {blocked ? null : (
            <NavigationBar
              scope={scope}
              navLoopId={loopId}
              loops={loops}
              runsByLoopId={runsByLoopId}
              totalLoopFindings={aggregateReading.totalFindings}
              onScopeChange={setScope}
              onLoopSwitcherOpenChange={setLoopSwitcherOpen}
              buckets={buckets}
              totalFindings={findings.length}
              objective={search.objective}
              onObjectiveChange={(objective) => setSearch({ objective, finding: undefined })}
              leading={runSentence}
              trailing={
                <span className="flex items-center gap-3">
                  {findingsViewPicker}
                  <span
                    className="hidden h-4.5 w-px shrink-0 bg-border-neutral-subtle min-[46rem]:inline-block"
                    aria-hidden="true"
                  />
                  {runsCog}
                </span>
              }
            />
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-6">
          <div className="flex flex-col gap-4">
            {/* Below the toolbar rather than above it: it names the loop the reading
                switched to, which only means anything once the controls row that did the switching
                is on screen. Suppressed for a switched-to loop's own run-state screens (no run,
                failed, generating, or a completed run with nothing in it): those screens already name
                the loop in their own copy (`noRunOtherLoop`, `failedOtherLoop`,
                `generatingOtherLoop`), so the banner would otherwise repeat that disclosure while also
                showing a hollow "Showing 0 findings" — not suppressed for the aggregate reading in
                general, which has no equivalent per-state copy naming "All loops" anywhere else,
                except the one aggregate case that now has its own screen (`aggregateEmpty`), which
                states the same fact this banner would and would otherwise show "Showing 0 findings
                across N loops" right above it. */}
            {navScope || (!isAggregate && blocked) || (isAggregate && aggregateEmpty) ? null : (
              <ContextBanner
                isAggregate={isAggregate}
                navLoopName={navLoopNameResolved}
                focusedLoopName={focusedLoopNameResolved}
                findingsCount={findings.length}
                readableLoopCount={aggregateReading.readableLoopCount}
                onReset={() => setScope(loopId)}
                onSwitchToThisLoop={() => switchToLoop(focusedLoopId)}
              />
            )}
            {blocked ?? reading()}
          </div>
        </div>
      </div>
    </FindingRefsProvider>
  );
};
